// ─── ImportFuelOrdersDialog — importar Excel de ordens → OTs fuel (Transfuel) ──
// O operador faz upload do(s) Excel que recebe dos clientes (cada um com o seu
// modelo de colunas). O backend (/fuel-import/parse) deteta o cabeçalho e sugere
// o mapeamento coluna→campo; aqui o operador CONFIRMA/AJUSTA esse mapeamento por
// ficheiro, pré-visualiza as OTs e cria. Multi-trip agrupado por Tour Nº no
// backend. Espelha o ImportGuiasDialog (Casais), em identidade "combustível".

import React, { useState, useContext, useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, CircularProgress, TextField, MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import TableChartIcon from '@material-ui/icons/TableChart';
import { AuthContext } from '../../App';

const C = {
  primary: '#16a34a', primaryDark: '#15803d', primarySoft: '#dcfce7',
  ink: '#0f172a', dim: '#64748b', muted: '#94a3b8', line: '#e8edf3', bg: '#f8fafc',
};

const useStyles = makeStyles(() => ({
  paper: { borderRadius: 16, overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 13, padding: '18px 22px', background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%)', borderBottom: `1px solid ${C.line}` },
  headerIcon: { width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${C.primary}, #22c55e)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, boxShadow: '0 6px 16px rgba(22,163,74,0.26)' },
  title: { fontSize: '1.12rem', fontWeight: 800, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1.15 },
  subtitle: { fontSize: '0.74rem', color: C.dim, marginTop: 1 },
  drop: { border: `2px dashed ${C.line}`, borderRadius: 14, padding: '30px 16px', textAlign: 'center', cursor: 'pointer', background: '#fff', transition: 'all 0.18s', '&:hover': { borderColor: C.primary, background: '#f0fdf4' } },
  dropActive: { borderColor: C.primary, background: '#dcfce7' },
  dropIcon: { width: 52, height: 52, borderRadius: '50%', background: C.primarySoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 },
  fileCard: { border: `1px solid ${C.line}`, borderRadius: 14, marginTop: 14, overflow: 'hidden' },
  fileHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: C.bg, borderBottom: `1px solid ${C.line}` },
  mapGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, padding: '14px 16px' },
  tableWrap: { border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', margin: '0 16px 14px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontSize: '0.58rem', fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 10px', textAlign: 'left', background: '#f8fafc', borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' },
  td: { fontSize: '0.74rem', color: C.ink, padding: '7px 10px', borderBottom: `1px solid #f1f5f9`, whiteSpace: 'nowrap' },
  banner: { padding: '12px 16px', borderRadius: 12, marginBottom: 12, fontSize: '0.8rem' },
  resultCard: { border: `1px solid ${C.line}`, borderRadius: 14, padding: 18, marginBottom: 14 },
  bigNum: { fontSize: '2rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em' },
  footer: { padding: '12px 20px', borderTop: `1px solid ${C.line}`, gap: 10 },
  primaryBtn: { background: C.primary, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: 10, padding: '7px 18px', boxShadow: '0 4px 14px rgba(22,163,74,0.22)', '&:hover': { background: C.primaryDark } },
  primaryBtnOff: { background: '#cbd5e1', color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: 10, padding: '7px 18px', boxShadow: 'none' },
  ghostBtn: { textTransform: 'none', fontWeight: 600, color: C.dim, borderRadius: 10 },
}));

// Deriva um "cliente" a partir do nome do ficheiro (1.ª palavra), ex.
// "Pumangol PBA Luanda.xlsx" → "pumangol".
function customerFromName(fileName) {
  const base = String(fileName || '').replace(/\.[a-z]+$/i, '').trim();
  const first = base.split(/[\s_\-]+/)[0] || base;
  return first.toLowerCase().slice(0, 30);
}

const PREVIEW_COLS = [
  { key: 'destino', label: 'Destino' },
  { key: 'produto', label: 'Produto' },
  { key: 'quantidade', label: 'Litros' },
  { key: 'ordemCarregamento', label: 'Ordem Carreg.' },
  { key: 'matricula', label: 'Matrícula' },
  { key: 'cisterna', label: 'Cisterna' },
  { key: 'motorista', label: 'Motorista' },
];

function ImportFuelOrdersDialog({ open, onClose, onImported, lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [files, setFiles] = useState([]);   // [{ fileName, columns, rows, mapping, customer, headerRowIndex }]
  const [fields, setFields] = useState([]); // [{key,label}] dos campos canónicos
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const h = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleFiles = useCallback(async (fileList) => {
    const list = Array.from(fileList || []).filter(f => /\.(xlsx|xlsm|xls)$/i.test(f.name));
    if (!list.length) { setError(pt ? 'Escolhe ficheiros Excel (.xlsx).' : 'Choose .xlsx files.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      list.forEach(f => fd.append('files', f));
      const r = await fetch('/planner-api/fuel-import/parse', { method: 'POST', headers: h, body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || (pt ? 'Falha a ler os ficheiros.' : 'Parse failed.'));
      setFields(data.fields || []);
      const parsed = (data.results || []).map(res => ({
        fileName: res.fileName,
        error: res.error || null,
        columns: res.columns || [],
        rows: res.rows || [],
        headerRowIndex: res.headerRowIndex,
        mapping: { ...(res.suggested || {}) },  // {campo: idx} editável
        customer: customerFromName(res.fileName),
      }));
      setFiles(prev => [...prev, ...parsed]);
    } catch (e) {
      setError(e.message || (pt ? 'Falha a ler os ficheiros.' : 'Parse failed.'));
    } finally { setLoading(false); }
  }, [h, pt]);

  const setMapping = (fi, fieldKey, colIdx) => {
    setFiles(prev => prev.map((f, i) => i !== fi ? f : {
      ...f, mapping: { ...f.mapping, [fieldKey]: colIdx === '' ? undefined : Number(colIdx) },
    }));
  };
  const setCustomer = (fi, value) => setFiles(prev => prev.map((f, i) => i !== fi ? f : { ...f, customer: value }));
  const removeFile = (fi) => setFiles(prev => prev.filter((_, i) => i !== fi));

  // Aplica o mapeamento de um ficheiro às suas linhas → linhas canónicas.
  const mappedRows = useCallback((file) => {
    return (file.rows || []).map(row => {
      const out = {};
      for (const key of Object.keys(file.mapping || {})) {
        const idx = file.mapping[key];
        if (idx == null) continue;
        const v = row.cells ? row.cells[idx] : undefined;
        if (v != null && String(v).trim() !== '') out[key] = v;
      }
      if (file.customer) out.fuelCustomer = file.customer;
      return out;
    });
  }, []);

  const validFiles = files.filter(f => !f.error && (f.rows || []).length);
  const totalOTs = useMemo(() => validFiles.reduce((s, f) => s + (f.rows || []).length, 0), [validFiles]);

  async function doImport() {
    if (!validFiles.length) return;
    setCreating(true); setError(null);
    const agg = { created: [], errors: [], grouped: 0, tours: 0 };
    try {
      for (const file of validFiles) {
        const rows = mappedRows(file);
        if (!rows.length) continue;
        const r = await fetch('/planner-api/fuel-import/create', {
          method: 'POST', headers: { ...h, 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows, fuelCustomer: file.customer || null }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { agg.errors.push({ ref: file.fileName, error: data.error || `HTTP ${r.status}` }); continue; }
        agg.created.push(...(data.created || []));
        agg.errors.push(...(data.errors || []));
        agg.grouped += data.grouped || 0; agg.tours += data.tours || 0;
      }
      setResult(agg);
      if (onImported) onImported(agg);
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao importar.' : 'Import error.'));
    } finally { setCreating(false); }
  }

  const reset = () => { setFiles([]); setResult(null); setError(null); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth classes={{ paper: classes.paper }}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}><LocalGasStationIcon /></Box>
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.title}>{pt ? 'Importar Excel → Ordens de Combustível' : 'Import Excel → Fuel Orders'}</Typography>
          <Typography className={classes.subtitle}>{pt ? 'Cada cliente tem o seu modelo; confirma o mapeamento das colunas e cria as OTs' : 'Confirm column mapping and create OTs'}</Typography>
        </Box>
        <Box style={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <DialogContent style={{ padding: '18px 22px' }}>
        {error && (
          <Box className={classes.banner} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ErrorIcon style={{ fontSize: 18 }} /> {error}
          </Box>
        )}

        {loading ? (
          <Box style={{ textAlign: 'center', padding: 44 }}><CircularProgress /></Box>

        /* ─── RESULTADO ─── */
        ) : result ? (
          <Box>
            <Box className={classes.resultCard} style={{ background: '#f0fdf4', borderColor: '#bbf7d0', display: 'flex', alignItems: 'center', gap: 16 }}>
              <Box style={{ width: 54, height: 54, borderRadius: 14, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircleIcon style={{ color: '#16a34a', fontSize: 30 }} />
              </Box>
              <Box>
                <Typography className={classes.bigNum} style={{ color: '#166534' }}>{result.created?.length || 0}</Typography>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#166534' }}>{pt ? 'OTs criadas' : 'OTs created'}</Typography>
              </Box>
              <Box style={{ flex: 1 }} />
              {result.grouped > 0 && (
                <Box style={{ textAlign: 'right' }}>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#15803d' }}>{result.tours} {pt ? 'viagens multi-trip' : 'multi-trips'}</Typography>
                  <Typography style={{ fontSize: '0.72rem', color: '#15803d' }}>{result.grouped} {pt ? 'OTs agrupadas por Tour Nº' : 'OTs grouped by Tour'}</Typography>
                </Box>
              )}
            </Box>
            {(result.errors || []).length > 0 && (
              <Box className={classes.banner} style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <Typography style={{ fontWeight: 700, color: '#92400e', marginBottom: 2 }}>{result.errors.length} {pt ? 'ignoradas / avisos' : 'skipped / warnings'}</Typography>
                <Box style={{ maxHeight: 140, overflowY: 'auto' }}>
                  {result.errors.map((e, i) => <Typography key={i} style={{ fontSize: '0.74rem', color: '#92400e' }}>• {e.ref || '—'}: {e.error}</Typography>)}
                </Box>
              </Box>
            )}
          </Box>

        /* ─── UPLOAD + MAPEAMENTO ─── */
        ) : (
          <>
            <label>
              <input type="file" multiple hidden accept=".xlsx,.xlsm,.xls" onChange={e => handleFiles(e.target.files)} />
              <Box
                className={`${classes.drop} ${dragOver ? classes.dropActive : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              >
                <Box className={classes.dropIcon}><CloudUploadIcon style={{ fontSize: 28, color: C.primary }} /></Box>
                <Typography style={{ fontWeight: 800, color: C.ink, fontSize: '0.92rem' }}>{pt ? 'Arrasta os ficheiros Excel das ordens para aqui' : 'Drop the order Excel files here'}</Typography>
                <Typography style={{ fontSize: '0.78rem', color: C.dim, marginTop: 2 }}>{pt ? 'ou clica para escolher · .xlsx' : 'or click to choose · .xlsx'}</Typography>
                <Typography style={{ fontSize: '0.7rem', color: C.muted, marginTop: 6 }}>{pt ? 'O cabeçalho e as colunas são detetados automaticamente' : 'Header and columns auto-detected'}</Typography>
              </Box>
            </label>

            {files.map((file, fi) => (
              <Box key={fi} className={classes.fileCard}>
                <Box className={classes.fileHead}>
                  <TableChartIcon style={{ fontSize: 18, color: file.error ? '#dc2626' : C.primary }} />
                  <Typography style={{ fontWeight: 800, fontSize: '0.86rem', color: C.ink }}>{file.fileName}</Typography>
                  {!file.error && (
                    <Typography style={{ fontSize: '0.72rem', color: C.dim }}>
                      · {(file.rows || []).length} {pt ? 'linhas' : 'rows'} · {pt ? 'cabeçalho L' : 'header L'}{file.headerRowIndex}
                    </Typography>
                  )}
                  <Box style={{ flex: 1 }} />
                  {!file.error && (
                    <TextField size="small" variant="outlined" label={pt ? 'Cliente' : 'Customer'} value={file.customer}
                      onChange={e => setCustomer(fi, e.target.value)} style={{ width: 150, marginRight: 8 }} />
                  )}
                  <IconButton size="small" onClick={() => removeFile(fi)}><CloseIcon style={{ fontSize: 16, color: C.muted }} /></IconButton>
                </Box>

                {file.error ? (
                  <Box style={{ padding: '14px 16px', color: '#991b1b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ErrorIcon style={{ fontSize: 18 }} /> {file.error}
                  </Box>
                ) : (
                  <>
                    {/* Mapeamento coluna → campo */}
                    <Box className={classes.mapGrid}>
                      {fields.map(f => (
                        <TextField key={f.key} select size="small" variant="outlined" label={f.label}
                          value={file.mapping[f.key] == null ? '' : String(file.mapping[f.key])}
                          onChange={e => setMapping(fi, f.key, e.target.value)}
                          InputLabelProps={{ shrink: true }}>
                          <MenuItem value=""><span style={{ color: C.muted }}>{pt ? '— ignorar —' : '— skip —'}</span></MenuItem>
                          {file.columns.map(col => (
                            <MenuItem key={col.idx} value={String(col.idx)}>{col.name}</MenuItem>
                          ))}
                        </TextField>
                      ))}
                    </Box>

                    {/* Pré-visualização (5 primeiras) */}
                    {(file.rows || []).length > 0 && (
                      <Box className={classes.tableWrap} style={{ overflowX: 'auto' }}>
                        <table className={classes.table}>
                          <thead><tr>
                            <th className={classes.th}>#</th>
                            {PREVIEW_COLS.map(c => <th key={c.key} className={classes.th}>{c.label}</th>)}
                          </tr></thead>
                          <tbody>
                            {mappedRows(file).slice(0, 5).map((r, i) => (
                              <tr key={i}>
                                <td className={classes.td} style={{ color: C.muted }}>{i + 1}</td>
                                {PREVIEW_COLS.map(c => (
                                  <td key={c.key} className={classes.td}>
                                    {r[c.key] != null && String(r[c.key]).trim() !== '' ? String(r[c.key]) : <span style={{ color: C.muted }}>—</span>}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.footer}>
        {result ? (
          <>
            <Button onClick={reset} className={classes.ghostBtn}>{pt ? 'Importar mais' : 'Import more'}</Button>
            <Button onClick={onClose} variant="contained" disableElevation className={classes.primaryBtn}>{pt ? 'Concluir' : 'Done'}</Button>
          </>
        ) : (
          <>
            <Box style={{ marginRight: 'auto', fontSize: '0.76rem', color: C.dim }}>
              {validFiles.length > 0
                ? <span><b style={{ color: C.ink }}>{totalOTs}</b> {pt ? 'OTs a criar' : 'OTs to create'} · {validFiles.length} {pt ? 'ficheiro(s)' : 'file(s)'}</span>
                : <span>{pt ? 'Escolhe os ficheiros Excel das ordens para começar' : 'Choose Excel order files to start'}</span>}
            </Box>
            <Button onClick={onClose} className={classes.ghostBtn}>{pt ? 'Cancelar' : 'Cancel'}</Button>
            <Button onClick={doImport} disabled={creating || !validFiles.length} variant="contained" disableElevation
              startIcon={creating ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckCircleIcon />}
              className={(validFiles.length && !creating) ? classes.primaryBtn : classes.primaryBtnOff}>
              {creating ? (pt ? 'A criar…' : 'Creating…') : (pt ? `Criar ${totalOTs} OT${totalOTs === 1 ? '' : 's'}` : `Create ${totalOTs}`)}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportFuelOrdersDialog;
